import * as React from 'react'
import clsx from 'clsx'
import { SelectClasses, SelectInputProps, SelectInput } from './SelectInput'
import { MenuProps } from '../Menu'
import { StandardInput, StandardInputProps } from '../StandardInput'
import { OutlinedInput } from '../OutlinedInput'
import { OutlinedDashInput } from '../OutlinedDashInput'
import { FilledInput } from '../FilledInput'
import { SxProps } from '../sx'
import {
  useFormControl,
  formControlState,
  FormControlProps,
} from '../FormControl'
import { useForkRef } from '../../hooks'
import { deepmerge } from '../../utils'
import CaretDownIcon from '../internal/svg-icons/CaretDown'
import { NativeSelectInput } from '../NativeSelect'
import style from './index.module.scss'
export interface SelectProps<T = unknown>
  extends Omit<SelectInputProps<T>, 'native' | 'value' | 'defaultValue'> {
  /**
   * If `true`, the width of the popover will automatically be set according to the items inside the
   * menu, otherwise it will be at least the width of the select input.
   * @default false
   */
  autoWidth?: boolean
  /**
   * The option elements to populate the select with.
   * Can be some `MenuItem` when `native` is false and `option` when `native` is true.
   *
   * ⚠️The `MenuItem` elements **must** be direct descendants when `native` is false.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   * @default {}
   */
  classes?: Partial<SelectClasses>
  /**
   * If `true`, the component is initially open. Use when the component open state is not controlled (i.e. the `open` prop is not defined).
   * You can only use it when the `native` prop is `false` (default).
   * @default false
   */
  defaultOpen?: boolean
  /**
   * The default value. Use when the component is not controlled.
   */
  defaultValue?: T
  /**
   * If `true`, a value is displayed even if no items are selected.
   *
   * In order to display a meaningful value, a function can be passed to the `renderValue` prop which
   * returns the value to be displayed when no items are selected.
   *
   * ⚠️ When using this prop, make sure the label doesn't overlap with the empty displayed value.
   * The label should either be hidden or forced to a shrunk state.
   * @default false
   */
  displayEmpty?: boolean
  /**
   * The icon that displays the arrow.
   * @default CaretDownIcon
   */
  IconComponent?: React.ElementType
  /**
   * The `id` of the wrapper element or the `select` element when `native`.
   */
  id?: string
  /**
   * An `Input` element; does not have to be a material-ui specific `Input`.
   */
  input?: React.ReactElement<any, any>
  /**
   * [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Attributes) applied to the `input` element.
   * When `native` is `true`, the attributes are applied on the `select` element.
   */
  inputProps?: StandardInputProps['inputProps']
  /**
   * The ID of an element that acts as an additional label. The Select will
   * be labelled by the additional label and the selected value.
   */
  labelId?: string
  /**
   * Props applied to the [`Menu`](/material-ui/api/menu/) element.
   */
  MenuProps?: Partial<MenuProps>
  /**
   * If `true`, `value` must be an array and the menu will support multiple selections.
   * @default false
   */
  multiple?: boolean
  /**
   * If `true`, the component uses a native `select` element.
   * @default false
   */
  native?: boolean
  /**
   * Callback fired when a menu item is selected.
   *
   * @param {SelectChangeEvent<T>} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (any).
   * **Warning**: This is a generic event, not a change event, unless the change event is caused by browser autofill.
   * @param {object} [child] The react element that was selected when `native` is `false` (default).
   */
  onChange?: SelectInputProps<T>['onChange']
  /**
   * Callback fired when the component requests to be closed.
   * Use it in either controlled (see the `open` prop), or uncontrolled mode (to detect when the Select collapses).
   *
   * @param {object} event The event source of the callback.
   */
  onClose?: (event: React.SyntheticEvent) => void
  /**
   * Callback fired when the component requests to be opened.
   * Use it in either controlled (see the `open` prop), or uncontrolled mode (to detect when the Select expands).
   *
   * @param {object} event The event source of the callback.
   */
  onOpen?: (event: React.SyntheticEvent) => void
  /**
   * If `true`, the component is shown.
   * You can only use it when the `native` prop is `false` (default).
   */
  open?: boolean
  /**
   * Render the selected value.
   * You can only use it when the `native` prop is `false` (default).
   *
   * @param {any} value The `value` provided to the component.
   * @returns {ReactNode}
   */
  renderValue?: (value: T) => React.ReactNode
  /**
   * Props applied to the clickable div element.
   */
  SelectDisplayProps?: React.HTMLAttributes<HTMLDivElement>
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The `input` value. Providing an empty string will select no options.
   * Set to an empty string `''` if you don't want any of the available options to be selected.
   *
   * If the value is an object it must have reference equality with the option in order to be selected.
   * If the value is not an object, the string representation must match with the string representation of the option in order to be selected.
   */
  value?: T | ''
  /**
   * The variant to use.
   * @default 'outlined'
   */
  variant?: FormControlProps['variant']
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  function Select(props, ref) {
    const {
      autoWidth = false,
      children,
      classes = {},
      className,
      defaultOpen = false,
      displayEmpty = false,
      IconComponent = CaretDownIcon,
      id,
      input,
      inputProps,
      labelId,
      MenuProps,
      multiple = false,
      native = false,
      onClose,
      onOpen,
      open,
      renderValue,
      SelectDisplayProps,
      placeholder,
      variant: variantProp = 'outlined',
      renderContentTop,
      renderContentBottom,
      hideSelectNav,
      ...other
    } = props

    const inputComponent = native ? NativeSelectInput : SelectInput

    const factorFormControl = useFormControl()
    const fcs = formControlState({
      props,
      factorFormControl,
      states: ['variant', 'error', 'focused', 'size', 'readOnly'],
    })

    const variant = (fcs.variant || variantProp) as FormControlProps['variant']

    const InputComponent =
      input ||
      {
        standard: <StandardInput />,
        outlined: <OutlinedInput />,
        filled: <FilledInput />,
        outlinedDash: <OutlinedDashInput />,
      }[variant!]

    // @ts-ignore
    const inputComponentRef = useForkRef(ref, InputComponent.ref)
    return (
      <React.Fragment>
        {React.cloneElement(InputComponent, {
          // Most of the logic is implemented in `SelectInput`.
          // The `Select` component is a simple API wrapper to expose something better to play with.
          inputComponent,
          classes: {
            ...InputComponent.props.classes,
            root: clsx(
              InputComponent.props?.classes?.root,
              style.selectInputRoot,
              style[`size-${fcs.size}`]
            ),
          },
          inputProps: {
            children,
            error: fcs.error,
            size: fcs.size,
            IconComponent,
            variant,
            type: undefined, // We render a select. We can ignore the type provided by the `Input`.
            multiple,
            ...(native
              ? { id }
              : {
                  autoWidth,
                  defaultOpen,
                  displayEmpty,
                  labelId,
                  MenuProps,
                  onClose,
                  onOpen,
                  open,
                  renderValue,
                  renderContentTop,
                  renderContentBottom,
                  placeholder: placeholder,
                  hideSelectNav,
                  SelectDisplayProps: { id, ...SelectDisplayProps },
                  readOnly: fcs.readOnly,
                }),
            ...inputProps,
            classes: {
              ...(inputProps
                ? deepmerge(classes, inputProps.classes)
                : classes),
            },
            ...(input ? input.props.inputProps : {}),
          },
          ref: inputComponentRef,
          className: clsx(InputComponent.props.className, className),
          // If a custom input is provided via 'input' prop, do not allow 'variant' to be propagated to it's root element. See https://github.com/mui/material-ui/issues/33894.
          ...(!input && { variant }),
          ...other,
        })}
      </React.Fragment>
    )
  }
)

// @ts-ignore - We add this property to find this component when wrapped in FormControl
Select.factorName = 'Select'
